/**
 * Utility functions for generating consistent space names across the application
 */

/**
 * Generates a standardized space name using the format:
 * "Subcategory for Capacity at Address" (if capacity > 1)
 * "Subcategory at Address" (if capacity <= 1)
 *
 * For search results, if includeHostName is true:
 * "Subcategory - Host Name" (just space type with host name)
 *
 * @param {Object} space - The space object
 * @param {Object} space.subCategory - The subcategory object
 * @param {string} space.subCategory.name - The subcategory name
 * @param {string} space.description - Fallback description
 * @param {number} space.capacity - The space capacity
 * @param {Object} space.organization - The organization object (optional)
 * @param {string} space.organization.name - The organization/host name
 * @param {string} space.organizationName - Fallback organization name
 * @param {Object} firstInstance - The first instance object (optional)
 * @param {Object} firstInstance.location - The location object
 * @param {Object} firstInstance.location.buildingAddress - The building address object
 * @param {string} firstInstance.location.buildingAddress.streetAddress - The street address
 * @param {string} firstInstance.location.name - Fallback location name
 * @param {string} space.city - Fallback city
 * @param {boolean} includeHostName - If true, returns "Subcategory - Host Name" format (for search results)
 * @returns {string} The formatted space name
 */
/**
 * Resolves a space's category to an uppercase string, handling both a direct
 * string field and a nested subCategory.category value.
 */
const getCategory = space => {
  const cat = space?.category || space?.subCategory?.category;
  if (typeof cat === "string") return cat;
  if (cat?.name) return cat.name;
  return "";
};

/**
 * Returns true when the space is a PLAY service.
 */
export const isPlaySpace = space => {
  return Boolean(space) && getCategory(space).toUpperCase() === "PLAY";
};

/**
 * Returns the full display name for a PLAY service: "Event Name - Host Name",
 * matching the "Subcategory - Host Name" convention used for regular spaces.
 * Returns null for non-PLAY spaces.
 */
export const getEventName = (space, firstInstance = null) => {
  if (!isPlaySpace(space)) return null;
  const inst = firstInstance || space?.instances?.[0];
  const eventName = inst?.name || inst?.title || space?.name || "";
  if (!eventName) return "";
  const hostName = space.hostDisplayName || space.organization?.name || space.organizationName;
  return hostName ? `${eventName} - ${hostName}` : eventName;
};

export const generateSpaceName = (space, firstInstance = null, includeHostName = false) => {
  // PLAY services are named by their event, not by "Subcategory at Address"
  const eventName = getEventName(space, firstInstance);
  if (eventName) return eventName;

  // Get subcategory name with fallbacks
  const subcategory = space.subCategory?.name || space.subCategoryName || space.description || "Space";

  // If includeHostName is true, return just "Subcategory - Host Name" format (for search results)
  // hostDisplayName takes precedence over the host organization name when set
  if (includeHostName) {
    const hostName = space.hostDisplayName || space.organization?.name || space.organizationName;
    return hostName ? `${subcategory} - ${hostName}` : subcategory;
  }

  // Get address with fallbacks
  const buildingAddress = firstInstance?.location?.buildingAddress;
  const address = buildingAddress?.streetAddress || firstInstance?.location?.name || space.city || "Unknown Location";

  // Get capacity
  const capacity = space.capacity || 0;

  // Generate name based on capacity
  if (capacity <= 1) {
    return `${subcategory} at ${address}`;
  }

  return `${subcategory} for ${capacity} at ${address}`;
};

/**
 * Generates a location string for display in cards and lists
 *
 * @param {Object} space - The space object
 * @param {Object} firstInstance - The first instance object (optional)
 * @param {Object} firstInstance.location - The location object
 * @param {Object} firstInstance.location.buildingAddress - The building address object
 * @param {string} firstInstance.location.buildingAddress.streetAddress - The street address
 * @param {string} firstInstance.location.name - Fallback location name
 * @param {string} space.city - Fallback city
 * @param {Object} space.country - The country object
 * @param {string} space.country.name - The country name
 * @returns {string} The formatted location string
 */
export const generateLocationString = (space, firstInstance = null) => {
  const buildingAddress = firstInstance?.location?.buildingAddress;

  if (buildingAddress) {
    const parts = [buildingAddress.buildingName, buildingAddress.city, buildingAddress.country].filter(Boolean);

    return parts.join(", ");
  }

  if (firstInstance?.location?.name) {
    return firstInstance.location.name;
  }

  // Fallback to city and country
  const parts = [space.city, space.country?.name].filter(Boolean);
  return parts.join(", ") || "Location not specified";
};

/**
 * Generates a capacity display string for UI components
 *
 * @param {number} capacity - The space capacity
 * @returns {string} The formatted capacity string
 */
export const generateCapacityDisplay = capacity => {
  if (!capacity || capacity <= 1) {
    return "";
  }
  return `${capacity} people`;
};

/**
 * Generates a complete space display name with capacity prefix
 * Used in cards and lists where we want "Capacity @ Location" format
 *
 * @param {Object} space - The space object
 * @param {Object} firstInstance - The first instance object (optional)
 * @returns {string} The formatted display string
 */
export const generateSpaceDisplayName = (space, firstInstance = null) => {
  const capacity = space.capacity || 0;
  const location = generateLocationString(space, firstInstance);

  if (capacity > 1) {
    return `${capacity} people @ ${location}`;
  }

  return location;
};
